// Movimientos de inventario y traspasos abiertos; cada escritura refresca lista, ficha y traspasos.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { inventoryWriteApi } from '@/api/services/retail-catalog/backoffice';
import type { MovementRequest } from '../lib/inventoryMovements';
import { INVENTORY_QUERY_KEY } from './useInventarioLens';
import { retryUnlessClientError } from './useInventorySku';

export const OPEN_TRANSFERS_KEY = [...INVENTORY_QUERY_KEY, 'transfers', 'OPEN'] as const;

export const ALL_TRANSFERS_KEY = [...INVENTORY_QUERY_KEY, 'transfers', 'ALL'] as const;

/** Traspasos abiertos de la cadena (`retail.inventory.lens.read`). */
export function useOpenTransfers(enabled: boolean) {
    return useQuery({
        queryKey: OPEN_TRANSFERS_KEY,
        queryFn: () => inventoryWriteApi.listTransfers('OPEN'),
        enabled,
        retry: retryUnlessClientError,
        staleTime: 30_000,
    });
}

/** Traspasos de la cadena en cualquier estado, para nombrar origen, destino y estado por `transferId`. */
export function useAllTransfers(enabled: boolean) {
    return useQuery({
        queryKey: ALL_TRANSFERS_KEY,
        queryFn: () => inventoryWriteApi.listTransfers(),
        enabled,
        retry: retryUnlessClientError,
        staleTime: 30_000,
    });
}

export function useRefreshInventory() {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: INVENTORY_QUERY_KEY });
}

/** Entrada, merma/ajuste o traspaso, según el tipo del formulario. */
export function useRegisterMovement() {
    const refresh = useRefreshInventory();
    return useMutation({
        mutationFn: ({
            request,
            key,
        }: {
            request: MovementRequest;
            key: string;
        }): Promise<unknown> => {
            const options = { idempotencyKey: key };
            if (request.kind === 'TRANSFER') {
                return inventoryWriteApi.createTransfer(request.body, options);
            }
            if (request.kind === 'ADJUSTMENT') {
                return inventoryWriteApi.registerAdjustment(request.body, options);
            }
            return inventoryWriteApi.registerEntry(request.body, options);
        },
        onSuccess: refresh,
    });
}

export type TransferDecision = 'receive' | 'cancel';

export function useSettleTransfer() {
    const refresh = useRefreshInventory();
    return useMutation({
        mutationFn: ({
            transferId,
            decision,
            key,
        }: {
            transferId: string;
            decision: TransferDecision;
            key: string;
        }) =>
            decision === 'receive'
                ? inventoryWriteApi.receiveTransfer(transferId, { idempotencyKey: key })
                : inventoryWriteApi.cancelTransfer(transferId, { idempotencyKey: key }),
        onSuccess: refresh,
    });
}
